import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { galleryImages, galleryCategories } from '../../mocks/gallery';

const GalleryPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('Tümü');
  const [selectedImage, setSelectedImage] = useState<typeof galleryImages[0] | null>(null);
  const [headerRef, headerInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const filteredImages = selectedCategory === 'Tümü'
    ? galleryImages
    : galleryImages.filter(img => img.category === selectedCategory);

  return (
    <div className="min-h-screen bg-cream-light pb-16 lg:pb-0">
      <Header />

      {/* Header Section */}
      <motion.section
        ref={headerRef}
        initial={{ opacity: 0, y: 30 }}
        animate={headerInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8 }}
        className="pt-32 pb-12 bg-white border-b border-gray-200"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-8">
            <h1 className="font-serif text-4xl lg:text-5xl font-bold text-navy mb-6">
              GALERİ
            </h1>
            
            {/* Filter Buttons */}
            <div className="flex flex-wrap justify-center gap-3">
              {galleryCategories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`font-sans text-sm font-medium px-6 py-2.5 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    selectedCategory === category
                      ? 'bg-navy text-white'
                      : 'bg-transparent text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </motion.section>

      {/* Gallery Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filteredImages.map((image, index) => (
              <motion.div
                key={image.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="relative group cursor-pointer overflow-hidden rounded-2xl"
                onClick={() => setSelectedImage(image)}
              >
                <img
                  src={image.image}
                  alt={image.title}
                  className="w-full h-80 object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
                  <i className="ri-zoom-in-line text-4xl text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300"></i>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center text-white hover:text-gold transition-colors duration-300 cursor-pointer"
              aria-label="Kapat"
            >
              <i className="ri-close-line text-3xl"></i>
            </button>

            <div className="max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
              <motion.img
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
                src={selectedImage.image}
                alt={selectedImage.title}
                className="w-full h-auto max-h-[85vh] object-contain rounded-lg"
              />
              <div className="text-center mt-6">
                <h3 className="font-serif text-2xl font-bold text-white mb-2">
                  {selectedImage.title}
                </h3>
                <p className="font-sans text-base text-gray-400">
                  {selectedImage.date}
                </p>
              </div>
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                const currentIndex = filteredImages.findIndex(img => img.id === selectedImage.id);
                const prevIndex = currentIndex > 0 ? currentIndex - 1 : filteredImages.length - 1;
                setSelectedImage(filteredImages[prevIndex]);
              }}
              className="absolute left-6 top-1/2 transform -translate-y-1/2 w-12 h-12 flex items-center justify-center text-white hover:text-gold transition-colors duration-300 cursor-pointer"
              aria-label="Önceki"
            >
              <i className="ri-arrow-left-line text-3xl"></i>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                const currentIndex = filteredImages.findIndex(img => img.id === selectedImage.id);
                const nextIndex = currentIndex < filteredImages.length - 1 ? currentIndex + 1 : 0;
                setSelectedImage(filteredImages[nextIndex]);
              }}
              className="absolute right-6 top-1/2 transform -translate-y-1/2 w-12 h-12 flex items-center justify-center text-white hover:text-gold transition-colors duration-300 cursor-pointer"
              aria-label="Sonraki"
            >
              <i className="ri-arrow-right-line text-3xl"></i>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
};

export default GalleryPage;